import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Tipos y llamadas de la pestaña «Archivos del día»: GENERACIONES de un rango de fechas (contrato /api/odoo/generaciones). */
export type ClaseArchivo = 'creditos' | 'pagos';
export type EstadoGeneracion = 'generando' | 'generado' | 'error' | 'interrumpido' | 'enviando' | 'enviado' | 'reenviado';

export type SeveridadProblema = 'bloquea' | 'aviso';
export interface ProblemaArchivo { codigo: string; severidad: SeveridadProblema; detalle: string; filas?: number[]; /** total real de filas (la lista `filas` viene acotada a 100) */ filas_total?: number; }

export interface ProgresoGeneracion {
  /** 'giros' | 'pagos' | 'revisando' | 'listo' (así lo da el back) */
  paso: string;
  dia_actual: string | null;
  dias_hechos: number;
  dias_total: number;
  pct: number;
}

export interface ResumenCreditos { nombre: string; filas: number; total: number; problemas: ProblemaArchivo[] | number; }
export interface DuplicadoOmitido { pago_id?: string; referencia?: string; credito_numero?: string | number; orden?: number; monto?: number; }
export interface ResumenPagos extends ResumenCreditos { duplicados_omitidos: number; duplicados_detalle?: DuplicadoOmitido[]; }

export interface ResultadoEnvioClase { estado: string; detalle?: string | null; ubicacion?: string | null; /** ya estaba entregado en un envío anterior */ previo?: boolean; }
export interface EnvioGeneracion {
  en: string;
  por: string | null;
  tipo: 'envio' | 'reenvio';
  ok: boolean;
  confirmo_3mit?: boolean;
  creditos: ResultadoEnvioClase | null;
  pagos: ResultadoEnvioClase | null;
}
export interface VersionGeneracion {
  en: string;
  por: string | null;
  tipo: 'generado' | 'regenerado' | 'reemplazado';
  archivo: ClaseArchivo | string | null;
  filas: number | null;
  total: number | null;
}

export interface Generacion {
  _id: string;
  desde: string;
  hasta: string;
  estado: EstadoGeneracion;
  progreso: ProgresoGeneracion | null;
  creditos: ResumenCreditos | null;
  pagos: ResumenPagos | null;
  envios: EnvioGeneracion[];
  origen: 'manual' | 'automatico' | string;
  createdAt: string;
  versiones?: VersionGeneracion[];
  error?: string | null;
}
export interface ListaGeneraciones { items: Generacion[]; total: number; }
export interface Solapes { dias_enviados: string[]; }

/** Revisión por tipo (rojo bloquea / ámbar aviso). El back la entrega en `problemas` de cada archivo. */
export interface OpcionesEnvio { forzar?: boolean; }
export interface ArchivoBajado { blob: Blob; nombre: string; }

export type TipoAviso = 'error' | 'envio_a_medias' | 'con_problemas' | 'interrumpido' | 'interrumpido_enviando' | 'falta_dia';
export interface AvisoGeneracion { generacion_id: string | null; desde: string; hasta: string; tipo: TipoAviso; mensaje: string; }
/** GET /generaciones/avisos. `proxima_subida`: instante ISO (asumido) o null. */
export interface Avisos { automatico: { encendido: boolean; hora_subida: number; proxima_subida: string | null }; avisos: AvisoGeneracion[]; }

export type FiltroEstado = '' | 'con_problema' | 'sin_enviar' | 'enviado';
export type EstadoFila = 'generado' | 'enviado' | 'retirado';
export interface FilaArchivo {
  fila: number; clave: string; credito_cuota: string; cedula: string; comercio: string; agencia?: string; monto: number;
  vence?: string | null; fecha_valor?: string | null; referencia?: string | null; metodo?: string | null; estado: EstadoFila; problemas: string[];
}
export interface ListaFilas { items: FilaArchivo[]; total: number; pagina: number; por_pagina: number; }
export interface FiltroFilas { q: string; comercio: string; soloProblemas: boolean; pagina: number; porPagina: number; }
export interface ComercioResumen { comercio: string; creditos: number; filas: number; total: number; }
export interface LadoResumen { por_comercio: ComercioResumen[]; filas: number; total: number; ya_enviadas: number; }
export interface ResumenGeneracion { creditos: LadoResumen; pagos: LadoResumen; }
export type QueHistorial = 'generado' | 'regenerado' | 'reemplazado' | 'envio' | 'reenvio' | 'intento_fallido' | 'interrumpido';
export interface EventoHistorial { en: string; por: string | null; que: QueHistorial; motivo?: string | null; detalle?: string | null; }

@Injectable({ providedIn: 'root' })
export class OdooArchivosService {
  private readonly base = `${environment.BASE_LA}/api/odoo`;
  constructor(private http: HttpClient) {}

  /** Avisos de la pestaña (qué atender) y el estado de la subida automática. */
  avisos(): Observable<Avisos> {
    return this.http.get<{ data: Avisos }>(`${this.base}/generaciones/avisos`).pipe(map((r) => r.data));
  }

  filas(id: string, tipo: ClaseArchivo, f: FiltroFilas): Observable<ListaFilas> {
    let params = new HttpParams().set('tipo', tipo).set('pagina', String(f.pagina)).set('por_pagina', String(f.porPagina));
    if (f.q.trim()) params = params.set('q', f.q.trim());
    if (f.comercio) params = params.set('comercio', f.comercio);
    if (f.soloProblemas) params = params.set('solo_problemas', '1');
    return this.http.get<{ data: ListaFilas }>(`${this.base}/generaciones/${encodeURIComponent(id)}/filas`, { params }).pipe(map((r) => r.data));
  }

  resumen(id: string): Observable<ResumenGeneracion> {
    return this.http.get<{ data: ResumenGeneracion }>(`${this.base}/generaciones/${encodeURIComponent(id)}/resumen`).pipe(map((r) => r.data));
  }

  historial(id: string): Observable<EventoHistorial[]> {
    return this.http.get<{ data: EventoHistorial[] }>(`${this.base}/generaciones/${encodeURIComponent(id)}/historial`).pipe(map((r) => r.data));
  }

  crear(desde: string, hasta: string): Observable<{ id: string }> {
    return this.http.post<{ data: { id: string } }>(`${this.base}/generaciones`, { desde, hasta }).pipe(map((r) => r.data));
  }

  /** `desde` y `hasta` (AAAA-MM-DD, opcionales) dejan solo las generaciones cuyo rango toca esas fechas. */
  lista(pagina: number, porPagina: number, desde = '', hasta = '', estado: FiltroEstado = ''): Observable<ListaGeneraciones> {
    let params = new HttpParams().set('pagina', String(pagina)).set('por_pagina', String(porPagina));
    if (desde) params = params.set('desde', desde);
    if (hasta) params = params.set('hasta', hasta);
    if (estado) params = params.set('estado', estado);
    return this.http.get<{ data: ListaGeneraciones }>(`${this.base}/generaciones`, { params }).pipe(map((r) => r.data));
  }

  detalle(id: string): Observable<Generacion> {
    return this.http.get<{ data: Generacion }>(`${this.base}/generaciones/${encodeURIComponent(id)}`).pipe(map((r) => r.data));
  }

  solapes(desde: string, hasta: string): Observable<Solapes> {
    const params = new HttpParams().set('desde', desde).set('hasta', hasta);
    return this.http.get<{ data: Solapes }>(`${this.base}/generaciones/solapes`, { params }).pipe(map((r) => r.data));
  }

  /** Baja el xlsx como blob; el nombre sale del encabezado Content-Disposition o, si no, del nombre del archivo. */
  descargar(id: string, tipo: ClaseArchivo, nombreArchivo = ''): Observable<ArchivoBajado> {
    return this.http.get(`${this.base}/generaciones/${encodeURIComponent(id)}/descargar/${tipo}`, { observe: 'response', responseType: 'blob' }).pipe(
      map((r: HttpResponse<Blob>) => ({ blob: r.body as Blob, nombre: nombreDeEncabezado(r.headers.get('Content-Disposition')) || nombreArchivo || `${tipo}.xlsx` })),
    );
  }

  /** El motivo es obligatorio (3 a 300 letras) y queda en el historial. */
  regenerar(id: string, motivo: string): Observable<{ id: string }> {
    return this.http.post<{ data: { id: string } }>(`${this.base}/generaciones/${encodeURIComponent(id)}/regenerar`, { motivo }).pipe(map((r) => r.data));
  }

  reemplazar(id: string, tipo: ClaseArchivo, archivo: File): Observable<Generacion> {
    const fd = new FormData();
    fd.append('archivo', archivo);
    return this.http.post<{ data: Generacion }>(`${this.base}/generaciones/${encodeURIComponent(id)}/reemplazar/${tipo}`, fd).pipe(map((r) => r.data));
  }

  enviar(id: string, opciones: OpcionesEnvio = {}): Observable<Generacion> {
    return this.http.post<{ data: Generacion }>(`${this.base}/generaciones/${encodeURIComponent(id)}/enviar`, opciones.forzar ? { forzar: true } : {}).pipe(map((r) => r.data));
  }

  reenviar(id: string, opciones: OpcionesEnvio = {}): Observable<Generacion> {
    const cuerpo: { confirmo_3mit: boolean; forzar?: boolean } = { confirmo_3mit: true };
    if (opciones.forzar) cuerpo.forzar = true;
    return this.http.post<{ data: Generacion }>(`${this.base}/generaciones/${encodeURIComponent(id)}/reenviar`, cuerpo).pipe(map((r) => r.data));
  }
}

/** «attachment; filename="GIROS_2026-10-01.xlsx"» → «GIROS_2026-10-01.xlsx» */
export function nombreDeEncabezado(cd: string | null): string {
  if (!cd) return '';
  const u = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (u) { try { return decodeURIComponent(u[1]); } catch { return u[1]; } }
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m ? m[1].trim() : '';
}

/** Problemas que el back devuelve con 409 al enviar con bloqueos ({bloqueado, detalle}), aplanados. */
export function problemasDeError(e: unknown): ProblemaArchivo[] | null {
  type Lado = { problemas?: ProblemaArchivo[] };
  const x = e as { status?: number; error?: { bloqueado?: boolean; problemas?: ProblemaArchivo[]; data?: { problemas?: ProblemaArchivo[] }; detalle?: { creditos?: Lado; pagos?: Lado } | ProblemaArchivo[] } };
  if (!x || x.status !== 409 || !x.error) return null;
  const d = x.error.detalle;
  if (Array.isArray(d)) return d;
  if (d) return [...((d.creditos && d.creditos.problemas) || []), ...((d.pagos && d.pagos.problemas) || [])];
  return x.error.problemas || (x.error.data && x.error.data.problemas) || null;
}

/** «Faltan 3 cédulas · filas 4, 9 y 12». */
export function filasTexto(filas: number[] | undefined, filasTotal?: number): string {
  if (!filas || !filas.length) return '';
  const v = filas.slice(0, 8).join(', ');
  const total = Math.max(filas.length, filasTotal || 0);
  return total > 8 ? `filas ${v}… (y ${total - 8} más)` : `${filas.length === 1 ? 'fila' : 'filas'} ${v}`;
}

/** Foco atrapado en un diálogo: Tab y Shift+Tab ciclan entre sus controles. */
export function atraparFoco(ev: KeyboardEvent, dialogo: HTMLElement): void {
  if (ev.key !== 'Tab') return;
  const f = Array.from(dialogo.querySelectorAll<HTMLElement>('button:not([disabled]), textarea, input:not([disabled]), select, a[href]'));
  if (!f.length) return;
  const primero = f[0], ultimo = f[f.length - 1], activo = document.activeElement;
  if (ev.shiftKey && activo === primero) { ev.preventDefault(); ultimo.focus(); }
  else if (!ev.shiftKey && activo === ultimo) { ev.preventDefault(); primero.focus(); }
  else if (!dialogo.contains(activo)) { ev.preventDefault(); primero.focus(); }
}

/** Etiqueta y estilo del estado de una generación (la misma en la tabla y en el detalle). */
export function chipGeneracion(g: Generacion): { texto: string; tipo: 'solid' | 'dash' | 'problem' | 'ghost' } {
  const ult = (g.envios || []).map((e) => e.en).filter(Boolean).sort().pop() || '';
  const hora = horaMinuto(ult);
  const sufijo = hora ? ` · ${hora}` : '';
  const chips: Record<EstadoGeneracion, [string, 'solid' | 'dash' | 'problem' | 'ghost']> = {
    generando: ['Generando', 'dash'],
    generado: ['Generado (sin enviar)', 'ghost'],
    error: ['Error', 'problem'],
    interrumpido: ['Interrumpido', 'problem'],
    enviando: ['Enviando…', 'dash'],
    enviado: [`Enviado a 3MIT${sufijo}`, 'solid'],
    reenviado: [`Reenviado a 3MIT${sufijo}`, 'solid'],
  };
  const [texto, tipo] = chips[g.estado] || [g.estado, 'ghost'];
  return { texto, tipo };
}

/** Rango corto: «1-oct al 3-oct» o «1-oct» (si es un solo día). */
export function rangoTexto(desde: string, hasta: string): string {
  const c = (y: string): string => { const p = y.slice(0, 10).split('-'); return `${Number(p[2])}-${MESES[Number(p[1]) - 1] || p[1]}`; };
  return desde === hasta ? c(desde) : `${c(desde)} al ${c(hasta)}`;
}
/** «14:05» en hora de Venezuela. */
export function horaMinuto(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Caracas' }) : '';
}

// ── Formato de fechas (hora de Venezuela) ──
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

function partes(ymd: string): { d: string; m: string; y: string; sem: string } {
  const [y, m, d] = ymd.slice(0, 10).split('-');
  const sem = DIAS[new Date(`${y}-${m}-${d}T00:00:00Z`).getUTCDay()];
  return { d, m: MESES[Number(m) - 1] || m, y, sem };
}
/** «Sábado 17-oct-2026» */
export function diaLargo(ymd: string): string { const p = partes(ymd); return `${cap(p.sem)} ${p.d}-${p.m}-${p.y}`; }
/** «martes 20-oct» */
export function diaCorto(ymd: string): string { const p = partes(ymd); return `${p.sem} ${p.d}-${p.m}`; }
/** «martes 06-oct» (sin año, para títulos del diálogo) */
export const diaMedio = diaCorto;

const fHora = new Intl.DateTimeFormat('es-VE', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Caracas' });
const fYmd = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas', year: 'numeric', month: '2-digit', day: '2-digit' });
/** «07-oct, 1:02 a. m.» */
export function momento(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = partes(fYmd.format(d));
  return `${p.d}-${p.m}, ${fHora.format(d)}`;
}
/** «07-oct a la 1:02 a. m.» */
export function momentoLargo(iso: string | null | undefined): string {
  const t = momento(iso);
  if (!t) return '';
  const [dia, hora] = t.split(', ');
  return `${dia} a ${/^1:/.test(hora) ? 'la' : 'las'} ${hora}`;
}
/** «1:00 a. m.» para una hora entera 0-23 */
export function horaEntera(h: number): string {
  const n = ((Math.trunc(h) % 24) + 24) % 24;
  return `${n % 12 === 0 ? 12 : n % 12}:00 ${n < 12 ? 'a. m.' : 'p. m.'}`;
}
export function diaVE(dias = 0): string { return fYmd.format(new Date(Date.now() + dias * 86400000)); }

const fMonto = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** «28,21 $» */
export const usd = (n: number | null | undefined): string => (typeof n === 'number' ? `${fMonto.format(n)} $` : '—');
export const num = (n: number | null | undefined): string => (typeof n === 'number' ? new Intl.NumberFormat('es-VE').format(n) : '—');

/** Códigos de problema de una fila, en español llano. */
export const TEXTO_PROBLEMA: Record<string, string> = {
  filas_repetidas: 'Fila repetida: 3MIT rechaza el archivo completo',
  cuotas_incompletas: 'Al crédito le faltan cuotas',
  cedula_invalida: 'Cédula inválida (letra + número sin ceros de relleno)',
  metodo_invalido: 'Método de pago no válido',
  comercio_vacio: 'Sin comercio: 3MIT usará el de omisión',
  filas_ya_enviadas: 'Esta fila ya se envió en otro lote',
  dias_ya_enviados: 'Este día ya se envió a 3MIT',
};
export const textoProblema = (c: string): string => TEXTO_PROBLEMA[c] || c;
